import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAppContext } from '@/context/AppContext';
import { useToken } from '@/lib/useToken';
import { apiFetch } from '@/lib/api';
import { CLIENTS } from '@/data/clients';
import type { Client } from '@/types/entities';

interface ApiClient {
  id: string;
  name: string;
  slug: string;
  description?: string | null;
  /** Task 201 D3 — taxonomy keywords from the API response. */
  keywords?: string[] | null;
  createdAt: string;
  updatedAt: string;
  systemOwned: boolean;
  archivedAt?: string | null;
  classifiedEventCount?: number;
  _count: { projects: number };
}

interface UseClientsOptions {
  /**
   * Include archived clients in the response. Default false — archived rows
   * are hidden unless the admin opts in via the "Show archived" toggle.
   */
  includeArchived?: boolean;
}

interface UseClientsResult {
  data: readonly Client[];
  isLoading: boolean;
}

export function useClients(opts: UseClientsOptions = {}): UseClientsResult {
  const { mockupMode, currentOrg } = useAppContext();
  const getToken = useToken();
  const orgId = currentOrg.id;
  const includeArchived = opts.includeArchived ?? false;

  const query = useQuery({
    queryKey: ['clients', orgId, { includeArchived }],
    queryFn: async () => {
      const token = await getToken();
      const listUrl = includeArchived
        ? `/api/v1/orgs/${orgId}/clients?includeArchived=true`
        : `/api/v1/orgs/${orgId}/clients`;
      const projUrl = includeArchived
        ? `/api/v1/orgs/${orgId}/projects?includeArchived=true`
        : `/api/v1/orgs/${orgId}/projects`;
      const res = await apiFetch<{ clients: ApiClient[] }>(listUrl, token);
      // Also fetch projects to embed in each client (matches frontend Client type).
      const projRes = await apiFetch<{
        projects: Array<{
          id: string;
          clientId: string;
          name: string;
          description?: string | null;
          billable: boolean;
          createdAt: string;
          systemOwned: boolean;
          archivedAt?: string | null;
          classifiedEventCount?: number;
        }>;
      }>(projUrl, token);

      return res.clients.map(
        (c): Client => ({
          id: c.id,
          name: c.name,
          businessUnitId: '',
          description: c.description ?? null,
          keywords: c.keywords ?? null,
          createdAt: c.createdAt,
          systemOwned: c.systemOwned,
          archivedAt: c.archivedAt ?? null,
          classifiedEventCount: c.classifiedEventCount ?? 0,
          projects: projRes.projects
            .filter((p) => p.clientId === c.id)
            .map((p) => ({
              id: p.id,
              clientId: p.clientId,
              name: p.name,
              description: p.description ?? null,
              type: p.billable ? ('billable' as const) : ('general' as const),
              createdAt: p.createdAt,
              systemOwned: p.systemOwned,
              archivedAt: p.archivedAt ?? null,
              classifiedEventCount: p.classifiedEventCount ?? 0,
            })),
        }),
      );
    },
    enabled: !mockupMode && !!orgId,
  });

  if (mockupMode) return { data: CLIENTS, isLoading: false };
  return { data: query.data ?? [], isLoading: query.isLoading };
}

// ── Mutation hooks ──────────────────────────────────────────────────────────
// Admin-only client CRUD. Each mutation invalidates the clients list (and
// projects + rules for archive/delete, which cascade). In mockup mode the
// mutation is a no-op — the fixture data is immutable.

export function useCreateClient() {
  const { currentOrg, mockupMode } = useAppContext();
  const getToken = useToken();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (body: { name: string; slug?: string; description?: string | null }) => {
      if (mockupMode) return undefined as unknown as ApiClient;
      const token = await getToken();
      return apiFetch<ApiClient>(`/api/v1/orgs/${currentOrg.id}/clients`, token, {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['clients', currentOrg.id] });
    },
  });
}

export function useUpdateClient() {
  const { currentOrg, mockupMode } = useAppContext();
  const getToken = useToken();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      ...body
    }: {
      id: string;
      name?: string;
      slug?: string;
      description?: string | null;
      /** Task 201 D3 — taxonomy keywords (≤20 items, each ≤32 chars). */
      keywords?: string[] | null;
    }) => {
      if (mockupMode) return undefined as unknown as ApiClient;
      const token = await getToken();
      return apiFetch<ApiClient>(`/api/v1/orgs/${currentOrg.id}/clients/${id}`, token, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['clients', currentOrg.id] });
    },
  });
}

export function useDeleteClient() {
  const { currentOrg, mockupMode } = useAppContext();
  const getToken = useToken();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      if (mockupMode) return undefined;
      const token = await getToken();
      return apiFetch<undefined>(`/api/v1/orgs/${currentOrg.id}/clients/${id}`, token, {
        method: 'DELETE',
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['clients', currentOrg.id] });
      void qc.invalidateQueries({ queryKey: ['projects', currentOrg.id] });
      void qc.invalidateQueries({ queryKey: ['rules', currentOrg.id] });
    },
  });
}

export function useArchiveClient() {
  const { currentOrg, mockupMode } = useAppContext();
  const getToken = useToken();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      if (mockupMode) return undefined as unknown as ApiClient;
      const token = await getToken();
      return apiFetch<ApiClient>(`/api/v1/orgs/${currentOrg.id}/clients/${id}/archive`, token, {
        method: 'POST',
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['clients', currentOrg.id] });
      void qc.invalidateQueries({ queryKey: ['projects', currentOrg.id] });
      void qc.invalidateQueries({ queryKey: ['rules', currentOrg.id] });
    },
  });
}

export function useRestoreClient() {
  const { currentOrg, mockupMode } = useAppContext();
  const getToken = useToken();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      if (mockupMode) return undefined as unknown as ApiClient;
      const token = await getToken();
      return apiFetch<ApiClient>(`/api/v1/orgs/${currentOrg.id}/clients/${id}/restore`, token, {
        method: 'POST',
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['clients', currentOrg.id] });
      void qc.invalidateQueries({ queryKey: ['projects', currentOrg.id] });
      void qc.invalidateQueries({ queryKey: ['rules', currentOrg.id] });
    },
  });
}
